import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserRound, CalendarDays, Palette, Smartphone, Archive, Palmtree, Bell, Home as HomeIcon, ArrowRight } from "lucide-react";

const ORANGE = "#e85122";

const Section = ({ title, children }) => (
  <div className="space-y-1.5">
    {title && <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: ORANGE }}>{title}</p>}
    <div className="text-sm leading-relaxed text-foreground/90 space-y-1.5">{children}</div>
  </div>
);
const List = ({ items }) => (
  <ul className="space-y-1">
    {items.map((t) => (
      <li key={t} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: ORANGE }} /><span>{t}</span></li>
    ))}
  </ul>
);

export const GUIDES = [
  {
    key: "profile", icon: UserRound, emoji: "👤", title: "MY PROFILE",
    desc: "View your employee information, designation, department and Reporting Person.",
    render: ({ user, reportingName }) => (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">Your profile contains your official LiveLife Education employee information.</p>
        <Section title="Your information">
          <List items={["Employee Name", "Employee ID", "Designation", "Department", "Sub-Department", "Reporting Person", "Role / Access", "Email", "Contact Information, where applicable"]} />
        </Section>
        <Section title="What you can edit">
          <p>You can edit only the information that LiveLife Education allows employees to maintain. For example, where enabled:</p>
          <List items={["Profile photo", "Contact information", "Other permitted personal details"]} />
        </Section>
        <Section title="What you cannot change">
          <p>The following are maintained by Admin:</p>
          <List items={["Employee ID", "Designation", "Department", "Sub-Department", "Reporting Person", "Role / Permissions", "Employment Status"]} />
        </Section>
        <Section title="My Reporting Person">
          <p>Your profile shows the person you report to.</p>
          <p className="font-semibold">Reporting Person: {reportingName || "Not yet assigned"}</p>
          <p>If you need support with your work, priorities, leave or responsibilities, your Reporting Person is your first point of contact unless otherwise communicated.</p>
        </Section>
      </div>
    ),
  },
  {
    key: "tracker", icon: CalendarDays, emoji: "📅", title: "DAILY TRACKER",
    desc: "Log your day, record your work, update activities and complete your daily status.",
    render: () => (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">The Daily Tracker helps you record your attendance and the work you actually do each day.</p>
        <Section title="1. Start your day">
          <p>Click <strong>+ Log Activity</strong>. Your first activity log is also your daily attendance / check-in. Your name is automatically recorded from your login.</p>
        </Section>
        <Section title="2. Attendance timing">
          <div className="rounded-lg border border-border p-3 space-y-1">
            <p><strong>Office Timing:</strong> 9:30 AM – 5:30 PM</p>
            <p><strong>Attendance Cut-off:</strong> 10:00 AM</p>
            <div className="pt-1 space-y-0.5 text-[13px]">
              <p>Before 10:00 AM — 🟢 ON TIME</p>
              <p>10:00 AM — 🟢 ON TIME</p>
              <p>After 10:00 AM — 🔴 LATE</p>
            </div>
            <p className="text-[12px] text-muted-foreground pt-1">After 10:00 AM, an Admin or authorized Reporting Person must open the daily log. The system records the actual first-log time.</p>
          </div>
        </Section>
        <Section title="3. Record your location">
          <p>Select <strong>OFFICE</strong> or <strong>FIELD</strong>. If you select FIELD, choose the relevant School / Field location.</p>
        </Section>
        <Section title="4. Record your work">
          <p>Add the activities you actually work on. Choose:</p>
          <p><strong>LINKED ASSET</strong> — for work connected to a Creative Work Item.</p>
          <p><strong>DIRECT TASK</strong> — for work not connected to a Creative Work Item, such as meetings, calls, planning, preparation, administration, travel or other legitimate work.</p>
        </Section>
        <Section title="5. Keep your activities updated">
          <p>Activity status: <strong>IN PROGRESS</strong> or <strong>COMPLETED</strong>. When you complete an activity, record the Outcome / What Was Achieved.</p>
        </Section>
        <Section title="6. Complete your daily update">
          <p>Before 5:30 PM, review and update your Daily Tracker. Make sure:</p>
          <List items={["Attendance is recorded", "Location is recorded", "All activities are logged", "Activity statuses are updated", "Completed work has an outcome", "Unfinished work is marked IN PROGRESS"]} />
        </Section>
        <Section title="7. Work not finished?">
          <p>That's okay. Leave the activity <strong>IN PROGRESS</strong> — it will carry forward to the next working day. Do not create a duplicate task.</p>
        </Section>
        <Section title="8. Review your day">
          <p>View your Daily Tracker by <strong>DAY | WEEK | MONTH</strong> to see attendance, first log time, ON TIME / LATE, location, activities, completed & in-progress work, carry-forward work, active time and outcomes.</p>
        </Section>
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 p-3 text-[13px]">
          <strong>Remember:</strong> Your responsibility is to keep your day's record updated. Every task does not have to be finished by 5:30 PM.
        </div>
      </div>
    ),
  },
  {
    key: "workflow", icon: Palette, emoji: "🎨", title: "CREATIVE WORKFLOW",
    desc: "Understand your assigned creative work and move it through the workflow according to your role.",
    render: () => (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">Creative Workflow shows where a piece of creative work is in the process.</p>
        <Section title="Our workflow">
          <p className="font-semibold">TO DO → PREPARE → CREATE → REVIEW → APPROVAL → COMPLETE</p>
        </Section>
        <Section title="When you work on Creative Work">
          <List items={["Open Daily Tracker", "Click + Log Activity", "Select LINKED ASSET", "Select the relevant Creative Work Item", "Record what you worked on", "Keep your activity status updated", "Follow the Creative Workflow according to your role"]} />
        </Section>
        <div className="rounded-lg border border-border p-3 text-[13px]">
          <strong>Important:</strong> Do not create a new Creative Asset just because you are working on an existing one.
        </div>
        <Section title="Remember the difference">
          <p><strong>Creative Workflow</strong> — where is the work? <strong>Daily Tracker</strong> — what did I work on today? They are connected, but serve different purposes.</p>
        </Section>
        <Section title="Workflow responsibility">
          <p>Depending on your role, you may prepare, create, review, correct, move forward or complete assigned work. Only move a stage when your role and permissions allow.</p>
        </Section>
      </div>
    ),
  },
  {
    key: "social", icon: Smartphone, emoji: "📱", title: "SOCIAL MEDIA",
    desc: "Manage assigned social content from planning to publishing.",
    render: () => (
      <div className="space-y-5">
        <Section title="Social Media follows">
          <p className="font-semibold">PLAN CONTENT → SCHEDULED → PUBLISHED</p>
        </Section>
        <Section title="If Social Media work is assigned to you">
          <List items={["Check your assigned content", "Prepare the content", "Update the content plan", "Schedule the content when required", "Publish when you are responsible for publishing", "Add the published link"]} />
        </Section>
        <div className="rounded-lg border border-border p-3 text-[13px] space-y-1">
          <p><strong>Social Media</strong> manages the content publishing process.</p>
          <p><strong>Daily Tracker</strong> records the work you actually performed today.</p>
          <p>Do not create duplicate Social Media records.</p>
        </div>
      </div>
    ),
  },
  {
    key: "archive", icon: Archive, emoji: "🗄️", title: "CREATIVE ARCHIVE",
    desc: "Understand how completed creative work is recorded and archived.",
    render: () => (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">Creative Archive is the permanent library of completed creative work.</p>
        <Section title="What is stored?">
          <List items={["Work Item", "Work Category", "Creative Type / Sub-category", "Creative Owner", "Completed Date", "Google Drive Source", "File Path / Location"]} />
        </Section>
        <Section title="When is work fully archived?">
          <p>Once a Creative Work Item is completed, a work item is fully archived when both the <strong>Google Drive Source</strong> and the <strong>File Path / Location</strong> are available.</p>
        </Section>
        <div className="rounded-lg border border-border p-3 text-[13px]">
          <strong>Important:</strong> Creative Archive is not another workflow stage. The Creative Work remains COMPLETE and is then recorded in the Archive.
        </div>
      </div>
    ),
  },
  {
    key: "leave", icon: Palmtree, emoji: "🏖️", title: "LEAVE",
    desc: "Apply for leave, check your balance and track your leave requests.",
    render: () => (
      <div className="space-y-5">
        <Section title="Your monthly leave">
          <p>Monthly entitlement: <strong>2 days</strong>. You can see your Leave Entitlement, Leave Used, Leave Remaining, Upcoming Leave and Leave Request Status.</p>
        </Section>
        <Section title="Applying for leave">
          <List items={["Open Leave", "Select the required date(s)", "Submit your request", "Track the approval status"]} />
        </Section>
        <Section title="If your regular leave is exhausted">
          <p>You may still submit an <strong>EXCESS LEAVE</strong> request. It requires Admin approval and can be Pending, Approved — Paid, Approved — LOP, or Rejected.</p>
        </Section>
        <div className="rounded-lg border border-border p-3 text-[13px]">
          <strong>Important:</strong> Do not use Daily Tracker to indicate leave. If you are taking leave, use the Leave process.
        </div>
      </div>
    ),
  },
  {
    key: "notifications", icon: Bell, emoji: "🔔", title: "NOTIFICATIONS",
    desc: "See assignments, approvals, corrections and other important updates.",
    render: () => (
      <div className="space-y-5">
        <Section title="Your notifications may include">
          <List items={["Work assigned to you", "Review requests", "Approval requests", "Corrections", "Leave updates", "Important announcements", "Other role-relevant updates"]} />
        </Section>
        <Section title="Notification bell">
          <p>The 🔔 icon shows your unread notification count. For example, 🔔 3 means you have 3 unread actionable notifications.</p>
        </Section>
        <p className="text-[13px] text-muted-foreground">Opening a notification marks it as READ. Reading it does not delete the notification history.</p>
      </div>
    ),
  },
  {
    key: "home", icon: HomeIcon, emoji: "🏠", title: "MY HOME",
    desc: "See your day, priorities, quick actions and important information.",
    render: () => (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">Your Home page gives you a quick view of what matters to you today.</p>
        <Section title="Today">
          <List items={["Attendance", "First Log Time", "Attendance Status", "Location", "My Work", "Today's Activities", "Completed Work", "In Progress Work", "Carry-forward Work", "Notifications"]} />
        </Section>
        <Section title="Quick Actions">
          <p>Depending on your access, you can quickly Log Activity, Apply Leave, View Notifications, Open My Profile, Open Creative Workflow, Open Social Media or Open Creative Archive.</p>
        </Section>
        <div className="rounded-lg border border-border p-3 text-[13px]">
          Use Home to understand what you need to do today — then go to the relevant module to complete the work.
        </div>
      </div>
    ),
  },
];

export default function EmployeeGuide({ user, reportingName }) {
  const [active, setActive] = useState(null);
  return (
    <section data-testid="employee-guide">
      <div className="mb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: ORANGE }}>Employee Guide</p>
        <h3 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mt-1">Know your workspace. Know what to do.</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
        {GUIDES.map((g) => {
          const Icon = g.icon;
          return (
            <button
              key={g.key}
              data-testid={`guide-card-${g.key}`}
              onClick={() => setActive(g)}
              className="group text-left rounded-2xl border border-border bg-card p-5 shadow-[0_1px_3px_rgba(16,24,40,0.05)] hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start gap-3">
                <span className="h-10 w-10 rounded-xl flex items-center justify-center text-lg shrink-0" style={{ background: `${ORANGE}14` }}>{g.emoji}</span>
                <div className="min-w-0">
                  <p className="font-display font-extrabold tracking-tight">{g.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{g.desc}</p>
                  <p className="text-xs font-semibold mt-3 flex items-center gap-1" style={{ color: ORANGE }}>View Guide <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`guide-modal-${active?.key || "none"}`}>
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-xl flex items-center justify-center text-xl" style={{ background: `${ORANGE}14` }}>{active.emoji}</span>
                  <DialogTitle className="font-display text-xl font-extrabold">{active.title}</DialogTitle>
                </div>
              </DialogHeader>
              <div className="mt-2">{active.render({ user, reportingName })}</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
